import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type MouseEvent,
} from "react";
import { formatDistanceToNow, parseISO } from "date-fns";
import {
  ArrowRight,
  Download,
  FileText,
  Loader2,
  Paperclip,
} from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { initials } from "@/lib/formatters";
import {
  getDownloadUrl,
  listAttachments,
} from "@/api/attachments";
import type { AttachmentRead, TaskRead } from "@/api/types";

// ─── Module-level popover cache ───────────────────────────────────────────
// One Map shared by every AttachmentsCell on the page. Caches the
// listAttachments response per task.id so a popover that gets reopened
// after a sort / filter / pagination round-trip serves instantly. The
// sheet calls invalidateAttachmentsCellCache(taskId) on upload / delete
// so the next badge click refetches.

const POPOVER_CACHE_LIMIT = 20;
const popoverCache = new Map<number, AttachmentRead[]>();

/** Push to the FIFO cache, evicting the oldest entry when over the
 *  limit. Map preserves insertion order — `delete()` of an existing
 *  key then `set()` would re-insert at the tail (refresh ordering),
 *  but here we just rely on insertion-order for FIFO eviction. */
function setCache(taskId: number, value: AttachmentRead[]): void {
  // Re-insert at the tail so a recently-fetched entry isn't the next
  // one evicted under sustained load.
  popoverCache.delete(taskId);
  popoverCache.set(taskId, value);
  if (popoverCache.size > POPOVER_CACHE_LIMIT) {
    const oldestKey = popoverCache.keys().next().value;
    if (oldestKey !== undefined) popoverCache.delete(oldestKey);
  }
}

/** Drop one task's cached attachment list. Called by the sheet after
 *  an upload or delete so the next badge click refetches the truth. */
export function invalidateAttachmentsCellCache(taskId: number): void {
  popoverCache.delete(taskId);
}

// ─── Component ────────────────────────────────────────────────────────────

interface AttachmentsCellProps {
  task: TaskRead;
  projectSlug: string;
  /** Called when the popover footer's "Open task →" link is clicked,
   *  so the parent can close the popover (it closes itself anyway
   *  via the Radix Popover state machine) and pop the task sheet. */
  onOpenTaskDetail?: (task: TaskRead) => void;
}

export function AttachmentsCell({
  task,
  projectSlug,
  onOpenTaskDetail,
}: AttachmentsCellProps) {
  const count = task.attachments_count;
  // "unknown — render nothing" semantics: undefined → empty cell.
  // Treat 0 the same way (no badge for zero — the cell is just a
  // visual hole for tasks that don't have files yet).
  if (typeof count !== "number" || count <= 0) {
    return <div className="w-[60px]" aria-hidden />;
  }

  return (
    <AttachmentsBadge
      task={task}
      projectSlug={projectSlug}
      count={count}
      onOpenTaskDetail={onOpenTaskDetail}
    />
  );
}

function AttachmentsBadge({
  task,
  projectSlug,
  count,
  onOpenTaskDetail,
}: {
  task: TaskRead;
  projectSlug: string;
  count: number;
  onOpenTaskDetail?: (task: TaskRead) => void;
}) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<AttachmentRead[] | null>(
    () => popoverCache.get(task.id) ?? null,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // One AbortController per in-flight fetch — aborted on popover
  // close or component unmount so a slow request doesn't try to
  // update state after the user has moved on.
  const inflightRef = useRef<AbortController | null>(null);

  const fetchList = useCallback(async () => {
    // Cache hit short-circuit. We still refresh `items` so a stale
    // closure doesn't render an outdated list (e.g. another
    // AttachmentsCell rendered first and populated the cache).
    const cached = popoverCache.get(task.id);
    if (cached) {
      setItems(cached);
      return;
    }
    inflightRef.current?.abort();
    const controller = new AbortController();
    inflightRef.current = controller;
    setLoading(true);
    setError(null);
    try {
      const res = await listAttachments(projectSlug, "task", task.id);
      if (controller.signal.aborted) return;
      setCache(task.id, res);
      setItems(res);
    } catch (err) {
      if (controller.signal.aborted) return;
      setError(extractMessage(err));
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [projectSlug, task.id]);

  // Trigger the fetch only when the popover actually opens. Closing
  // the popover aborts any in-flight request so the user doesn't
  // pay for a network round-trip they cancelled mid-flight.
  useEffect(() => {
    if (!open) {
      inflightRef.current?.abort();
      inflightRef.current = null;
      return;
    }
    void fetchList();
  }, [open, fetchList]);

  // On unmount: abort whatever's in flight.
  useEffect(() => {
    return () => {
      inflightRef.current?.abort();
    };
  }, []);

  /** Resolve a signed URL and pop it in a new tab. Used for the
   *  per-row download icon. Errors are surfaced inline (we don't
   *  toast because the popover is the focal point — the user is
   *  about to see the failure). */
  const handleDownload = useCallback(
    async (attachmentId: string) => {
      try {
        const res = await getDownloadUrl(
          projectSlug,
          "task",
          task.id,
          attachmentId,
        );
        window.open(res.url, "_blank", "noopener,noreferrer");
      } catch (err) {
        setError(extractMessage(err));
      }
    },
    [projectSlug, task.id],
  );

  // Edge case: if the cached/fetched list comes back empty (e.g.
  // another tab deleted everything between badge render and popover
  // open) close the popover instead of showing an awkward "0 files"
  // shell — the badge will hide itself on the next list refetch.
  useEffect(() => {
    if (!open) return;
    if (loading) return;
    if (items !== null && items.length === 0) {
      setOpen(false);
    }
  }, [open, loading, items]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          // The badge sits inside parents that ALSO own click
          // handlers (TaskItem's middle button opens the sheet);
          // stopPropagation keeps a badge click from also opening
          // the sheet underneath the popover.
          onClick={(e: MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
          }}
          aria-label={`${count} attachment${count === 1 ? "" : "s"}`}
          className={cn(
            "inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-200",
            open && "bg-slate-200",
          )}
        >
          <Paperclip className="h-3 w-3" />
          <span className="tabular-nums">{count}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={6}
        className="w-80 p-0"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-slate-900/[0.06] px-3 py-2">
          <h4 className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
            Attachments
          </h4>
        </div>
        {loading && items === null ? (
          <PopoverListSkeleton />
        ) : error ? (
          <div className="p-3 text-xs text-rose-600">
            {error}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="ml-2 h-7 px-2 text-xs"
              onClick={() => void fetchList()}
            >
              Retry
            </Button>
          </div>
        ) : items && items.length > 0 ? (
          <ul className="max-h-72 overflow-y-auto py-1">
            {items.map((att) => (
              <li
                key={att.id}
                className="flex items-center gap-2 px-3 py-1.5"
              >
                <span
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-slate-100 text-slate-500"
                  aria-hidden
                >
                  <FileText className="h-3.5 w-3.5" />
                </span>
                <div className="min-w-0 flex-1">
                  <p
                    className="truncate text-xs text-slate-800"
                    title={att.filename}
                  >
                    {att.filename}
                  </p>
                  <p className="truncate text-[10px] text-slate-400">
                    {formatMeta(att)}
                  </p>
                </div>
                {att.uploader_name && (
                  <Avatar className="h-5 w-5 shrink-0 border border-slate-200">
                    <AvatarFallback className="bg-slate-900 text-[9px] font-semibold text-white">
                      {initials(att.uploader_name)}
                    </AvatarFallback>
                  </Avatar>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 shrink-0 p-0 text-slate-400 hover:text-slate-700"
                  onClick={() => void handleDownload(att.id)}
                  aria-label={`Download ${att.filename}`}
                  title="Download"
                >
                  <Download className="h-3.5 w-3.5" />
                </Button>
              </li>
            ))}
          </ul>
        ) : null}
        {onOpenTaskDetail && (
          <div className="border-t border-slate-900/[0.06] px-3 py-2">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
                onOpenTaskDetail(task);
              }}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-indigo-600 transition-colors hover:text-indigo-500"
            >
              Open task
              <ArrowRight className="h-3 w-3" />
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

function PopoverListSkeleton() {
  return (
    <ul className="py-1">
      {Array.from({ length: 3 }).map((_, i) => (
        <li key={i} className="flex items-center gap-2 px-3 py-1.5">
          <div className="h-7 w-7 shrink-0 animate-pulse rounded-md bg-slate-200" />
          <div className="min-w-0 flex-1 space-y-1.5">
            <div className="h-3 w-2/3 animate-pulse rounded bg-slate-200" />
            <div className="h-2 w-1/2 animate-pulse rounded bg-slate-100" />
          </div>
          <div className="h-5 w-5 shrink-0 animate-pulse rounded-full bg-slate-200" />
        </li>
      ))}
      <li className="flex items-center justify-center px-3 py-2 text-[11px] text-slate-400">
        <Loader2 className="mr-2 h-3 w-3 animate-spin" />
        Loading…
      </li>
    </ul>
  );
}

function formatMeta(att: AttachmentRead): string {
  const parts: string[] = [formatBytes(att.size_bytes)];
  if (att.uploader_name) {
    // First name + last initial — keeps the meta line compact in
    // the narrow popover (320px wide).
    const segs = att.uploader_name.trim().split(/\s+/);
    if (segs.length === 1) {
      parts.push(segs[0]);
    } else if (segs.length >= 2) {
      parts.push(`${segs[0]} ${segs[segs.length - 1].charAt(0)}.`);
    }
  }
  if (att.created_at) {
    try {
      parts.push(
        formatDistanceToNow(parseISO(att.created_at), { addSuffix: true }),
      );
    } catch {
      // Bad ISO → silent skip rather than a crashed render.
    }
  }
  return parts.join(" · ");
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function extractMessage(err: unknown): string {
  if (err && typeof err === "object" && "response" in err) {
    const res = (err as { response?: { data?: unknown } }).response;
    if (res && typeof res === "object" && "data" in res) {
      const data = (res as { data?: unknown }).data;
      if (data && typeof data === "object" && "detail" in data) {
        const detail = (data as { detail?: unknown }).detail;
        if (typeof detail === "string") return detail;
      }
    }
  }
  return "Couldn't load attachments.";
}
